import { useMemo, useState, useRef, useEffect } from 'react'
import {
  X,
  Calendar,
  TrendingUp,
  TrendingDown,
  Minus,
  Edit2,
  Languages,
} from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { getAvatarColor, getInitials } from '@/lib/utils/avatarUtils'
import { formatDistanceToNow } from 'date-fns'
import type { Insight } from '@/lib/mock-data'
import { cn } from '@/lib/utils'
import { getDateLocale } from '@/lib/utils/dateLocale'
import { TranslatableText } from '@/components/TranslatableText'
import { useTranslation } from 'react-i18next'

const LANGUAGE_MAP: Record<string, string> = {
  English: 'en',
  Español: 'es',
  Français: 'fr',
  Deutsch: 'de',
  中文: 'zh',
}

const LANGUAGE_OPTIONS = Object.keys(LANGUAGE_MAP)

const CODE_TO_LABEL = Object.fromEntries(
  Object.entries(LANGUAGE_MAP).map(([label, code]) => [code, label])
)

interface ProfileSheetProps {
  open: boolean
  onClose: () => void
  userId: string
  username: string
  displayName?: string
  bio?: string
  hasPhone?: boolean
  insights: Insight[]
  isCurrentUser: boolean
  onEditProfile?: () => void
}

export function ProfileSheet({
  open,
  onClose,
  userId,
  username,
  displayName,
  bio: bioProp,
  hasPhone = true,
  insights,
  isCurrentUser,
  onEditProfile,
}: ProfileSheetProps) {
  const { t, i18n } = useTranslation()
  // Get user's insights
  const userInsights = useMemo(
    () => insights.filter((i) => i.userId === userId).slice(0, 5),
    [insights, userId]
  )

  // Calculate stats
  const stats = useMemo(() => {
    const userInsightsAll = insights.filter((i) => i.userId === userId)
    return {
      total: userInsightsAll.length,
      bullish: userInsightsAll.filter((i) => i.sentiment === 'bullish').length,
      bearish: userInsightsAll.filter((i) => i.sentiment === 'bearish').length,
      neutral: userInsightsAll.filter((i) => i.sentiment === 'neutral').length,
    }
  }, [insights, userId])

  // Bio from props (passed from FeedPage via useProfile hook)
  const bio = bioProp || ''

  // Get user's first insight date
  const joinedDate = useMemo(() => {
    const userInsightsAll = insights.filter((i) => i.userId === userId)
    if (userInsightsAll.length === 0) return null

    const oldest = userInsightsAll.reduce((oldest, insight) => {
      const insightDate =
        typeof insight.createdAt === 'string'
          ? new Date(insight.createdAt)
          : insight.createdAt
      const oldestDate =
        typeof oldest.createdAt === 'string'
          ? new Date(oldest.createdAt)
          : oldest.createdAt
      return insightDate < oldestDate ? insight : oldest
    })

    return typeof oldest.createdAt === 'string'
      ? new Date(oldest.createdAt)
      : oldest.createdAt
  }, [insights, userId])

  const avatarColor = getAvatarColor(username)
  const initials = getInitials(displayName || '', username)
  const finalDisplayName = displayName || username

  // Check if user is admin (from any of their insights)
  const isAdmin = userInsights.some((i) => i.isAdmin)

  const [languageMenuOpen, setLanguageMenuOpen] = useState(false)
  const [selectedLanguage, setSelectedLanguage] = useState(
    () => CODE_TO_LABEL[i18n.language] || 'English'
  )
  const languageRef = useRef<HTMLDivElement>(null)

  const handleLanguageChange = (lang: string) => {
    setSelectedLanguage(lang)
    setLanguageMenuOpen(false)
    i18n.changeLanguage(LANGUAGE_MAP[lang])
  }

  // Reset dropdown when the sheet closes or reopens
  useEffect(() => {
    if (!open) {
      setLanguageMenuOpen(false)
    }
  }, [open])

  // Close dropdown when clicking outside (only while it is open)
  useEffect(() => {
    if (!languageMenuOpen) return

    const handleDocumentClick = (event: MouseEvent) => {
      if (
        languageRef.current &&
        !languageRef.current.contains(event.target as Node)
      ) {
        setLanguageMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', handleDocumentClick)
    return () => document.removeEventListener('mousedown', handleDocumentClick)
  }, [languageMenuOpen])

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent className="bg-neutral-950 border-white/[0.06] flex flex-col h-full max-h-[90vh] w-full sm:max-w-md overflow-hidden">
        {/* Header with Close Button */}
        <SheetHeader className="flex-shrink-0 flex flex-row items-center justify-between pb-4 border-b border-white/[0.06]">
          <SheetTitle className="text-xl font-bold text-white">
            {isCurrentUser ? t('editProfile') : t('profile')}
          </SheetTitle>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-neutral-400 hover:text-white hover:bg-white/[0.06] h-8 w-8 transition-colors"
          >
            <X className="w-5 h-5" />
          </Button>
        </SheetHeader>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto py-6 px-6 space-y-6">
          {/* Profile Info Section */}
          <div className="text-center space-y-4 animate-in fade-in zoom-in duration-300">
            {/* Avatar */}
            <div
              className={`w-24 h-24 rounded-full bg-gradient-to-br ${avatarColor} mx-auto flex items-center justify-center shadow-xl ring-4 ring-neutral-900`}
            >
              <span className="text-black text-3xl font-bold">{initials}</span>
            </div>

            {/* Display Name & Username */}
            <div>
              <div className="flex items-center justify-center gap-2 mb-1">
                <h2 className="text-2xl font-bold text-white">
                  {finalDisplayName}
                </h2>
                {isAdmin && (
                  <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-xs font-medium">
                    {t('admin')}
                  </span>
                )}
              </div>
              <p className="text-neutral-400 text-sm font-mono">@{username}</p>
            </div>

            {/* Joined Date */}
            {joinedDate && (
              <div className="flex items-center justify-center gap-2 text-neutral-500 text-sm">
                <Calendar className="w-4 h-4" />
                <span>
                  {t('joined', {
                    time: formatDistanceToNow(joinedDate, {
                      addSuffix: true,
                      locale: getDateLocale(i18n.language),
                    }),
                  })}
                </span>
              </div>
            )}

            {/* Bio */}
            {bio && (
              <p className="text-neutral-300 text-sm leading-relaxed max-w-sm mx-auto px-4">
                <TranslatableText text={bio} />
              </p>
            )}

            <div className="flex gap-3 justify-center">
              {/* Edit Profile Button (if viewing own profile) */}
              {isCurrentUser && onEditProfile && (
                <div className="relative">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onEditProfile}
                    className={`bg-neutral-900/50 border-white/[0.08] text-neutral-300 hover:bg-neutral-800 hover:text-white gap-2 transition-colors ${
                      !hasPhone ? 'border-amber-500/30' : ''
                    }`}
                  >
                    <Edit2 className="w-4 h-4" />
                    {t('editProfile')}
                  </Button>
                  {!hasPhone && (
                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-amber-500 rounded-full ring-2 ring-neutral-950 animate-pulse" />
                  )}
                </div>
              )}

              {/* Language Dropdown */}
              {isCurrentUser && (
                <div className="relative" ref={languageRef}>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setLanguageMenuOpen((prev) => !prev)}
                    className="bg-neutral-900/50 border-white/[0.08] text-neutral-300 hover:bg-neutral-800 hover:text-white gap-2 transition-colors"
                  >
                    <Languages className="w-4 h-4" />
                    {selectedLanguage}
                  </Button>

                  {languageMenuOpen && (
                    <div className="absolute right-0 mt-2 w-44 bg-neutral-950 border border-white/[0.08] rounded-xl shadow-2xl shadow-black/50 backdrop-blur-xl z-50">
                      <div className="px-3 py-2 border-b border-white/[0.06]">
                        <p className="text-[10px] font-semibold text-neutral-500 uppercase tracking-wider">
                          {t('selectLanguage')}
                        </p>
                      </div>
                      <div className="py-1">
                        {LANGUAGE_OPTIONS.map((lang) => (
                          <button
                            key={lang}
                            onClick={() => handleLanguageChange(lang)}
                            className="w-full text-left px-3 py-2.5 text-sm text-neutral-300 hover:text-white hover:bg-white/[0.04] transition-colors"
                          >
                            {lang}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-4 gap-3">
            {/* Total Insights */}
            <div className="bg-neutral-900/50 rounded-lg p-3 border border-white/[0.06] text-center">
              <div className="text-2xl font-bold text-white mb-1">
                {stats.total}
              </div>
              <div className="text-xs text-neutral-400">{t('insights')}</div>
            </div>

            {/* Bullish */}
            <div className="bg-emerald-500/10 rounded-lg p-3 border border-emerald-500/20 text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <TrendingUp className="w-4 h-4 text-emerald-400" />
                <div className="text-2xl font-bold text-emerald-400">
                  {stats.bullish}
                </div>
              </div>
              <div className="text-xs text-emerald-400/70">{t('bullish')}</div>
            </div>

            {/* Bearish */}
            <div className="bg-red-500/10 rounded-lg p-3 border border-red-500/20 text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <TrendingDown className="w-4 h-4 text-red-400" />
                <div className="text-2xl font-bold text-red-400">
                  {stats.bearish}
                </div>
              </div>
              <div className="text-xs text-red-400/70">{t('bearish')}</div>
            </div>

            {/* Neutral */}
            <div className="bg-neutral-500/10 rounded-lg p-3 border border-neutral-500/20 text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <Minus className="w-4 h-4 text-neutral-400" />
                <div className="text-2xl font-bold text-neutral-300">
                  {stats.neutral}
                </div>
              </div>
              <div className="text-xs text-neutral-400/70">{t('neutral')}</div>
            </div>
          </div>

          {/* Recent Insights Section */}
          {userInsights.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-neutral-400 uppercase tracking-wide">
                {t('recentInsights')}
              </h3>

              <div className="space-y-3">
                {userInsights.map((insight) => (
                  <div
                    key={insight.id}
                    className="bg-neutral-900/30 rounded-lg p-3 border border-white/[0.06] hover:bg-neutral-800/50 hover:border-white/[0.1] transition-all cursor-pointer group"
                    onClick={onClose}
                  >
                    {/* Ticker & Sentiment Row */}
                    <div className="flex items-center gap-2 mb-2">
                      {insight.ticker && (
                        <span className="px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-400 text-xs font-semibold border border-cyan-500/20 font-mono">
                          ${insight.ticker}
                        </span>
                      )}
                      <div
                        className={cn(
                          'flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium',
                          insight.sentiment === 'bullish' &&
                            'bg-emerald-500/20 text-emerald-400 border border-emerald-500/20',
                          insight.sentiment === 'bearish' &&
                            'bg-red-500/20 text-red-400 border border-red-500/20',
                          insight.sentiment === 'neutral' &&
                            'bg-neutral-500/20 text-neutral-400 border border-neutral-500/20'
                        )}
                      >
                        {insight.sentiment === 'bullish' && (
                          <TrendingUp className="w-3 h-3" />
                        )}
                        {insight.sentiment === 'bearish' && (
                          <TrendingDown className="w-3 h-3" />
                        )}
                        {insight.sentiment === 'neutral' && (
                          <Minus className="w-3 h-3" />
                        )}
                        {t(insight.sentiment)}
                      </div>
                      <span className="text-xs text-neutral-500 ml-auto font-mono">
                        {formatDistanceToNow(
                          typeof insight.createdAt === 'string'
                            ? new Date(insight.createdAt)
                            : insight.createdAt,
                          {
                            addSuffix: true,
                            locale: getDateLocale(i18n.language),
                          }
                        )}
                      </span>
                    </div>

                    {/* Content */}
                    <p className="text-sm text-neutral-300 line-clamp-2 group-hover:text-white transition-colors">
                      <TranslatableText text={insight.content} />
                    </p>
                  </div>
                ))}
              </div>

              {/* View All Link */}
              {stats.total > 5 && (
                <button
                  onClick={onClose}
                  className="text-sm text-emerald-400 hover:text-emerald-300 transition-colors w-full text-center py-2"
                >
                  {t('viewAllInsights', { count: stats.total })} →
                </button>
              )}
            </div>
          )}

          {/* No Insights State */}
          {userInsights.length === 0 && (
            <div className="text-center py-8">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-neutral-900/50 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-neutral-500" />
              </div>
              <p className="text-neutral-400 text-sm">{t('noInsightsYet')}</p>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
